import { useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../ampli';
import { Button } from '../../../components/common/buttons/Button';
import { Chip } from '../../../components/common/chip/Chip';
import { MainHead } from '../../../components/common/head/MainHead';
import { Icon } from '../../../components/common/icon/Icon';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import { MachineApprovalStatus } from '../../../constants/machine/machine.constant';
import { pageInfo, sellTractorPageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { usePatchMyOwnTractorsMutation } from '../../../hooks/api/user/mutations/usePatchMyOwnTractors.mutation';
import { useGetMyOwnTractorById } from '../../../hooks/api/user/queries/useGetMyOwnTractorById';
import { useUpdateUserHasNewPoke } from '../../../hooks/browserStorage/useUpdateBrowserStorage';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useAppDispatch } from '../../../hooks/store/useStore';
import { IMachine } from '../../../interfaces/Machine.interfaces';
import { ITractorSuggestedPrice } from '../../../interfaces/OwnTractor.interfaces';
import { userOwnTractorSurveySlice, userOwnTractorSurveyStateType } from '../../../store/userOwnTractorSurveySlice';
import { userSellTractorSurveySlice } from '../../../store/userSellTractorSurveySlice';
import { formatDateFromTodayOnlyDifference } from '../../../utils/formatter/formatDateFromTodayOnlyDifference.util';
import { formatPrice } from '../../../utils/formatter/formatPrice.util';
import { getOpenMarketProductTitle } from '../../../utils/getter/getOpenMarketProductTitle.util';
import { FeedbackPopupBody } from './components/FeedbackPopupBody';

export const MyInfoOwnTractorDetailPage = () => {
  const navigate = useNavigate();
  const { tractorId } = useParams();
  const { APPROVAL, PSWAITING } = MachineApprovalStatus;

  const [selectedSuggestedPrice, setSelectedSuggestedPrice] = useState<ITractorSuggestedPrice | null>();
  const [isPriceSuggestModalOpen, setIsPriceSuggestModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const dispatch = useAppDispatch();
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;
  const userOwnTractorActions = userOwnTractorSurveySlice.actions;

  const {
    data: myOwnTractor,
    isLoading: isGetUsersOwnTractorLoading,
    isSuccess: isGetUsersOwnTractorSuccess,
    refetch: refetchMyOwnTractor,
  } = useGetMyOwnTractorById({
    ownTractorId: tractorId!,
  });

  const { mutate: patchUsersOwnTractor } = usePatchMyOwnTractorsMutation();
  const updateUserHasNewPoke = useUpdateUserHasNewPoke({ shouldFetch: isGetUsersOwnTractorSuccess });

  const pokingDetailTitle = useMemo(() => {
    if (!myOwnTractor) {
      return '';
    }
    return getOpenMarketProductTitle({
      brandName: myOwnTractor.brand?.name,
      machine: myOwnTractor.machine as IMachine,
      showPsOnPendingApproval: true,
    });
  }, [myOwnTractor]);

  const handleEditOwnTractor = () => {
    if (!myOwnTractor || !myOwnTractor.id) {
      return;
    }

    const payload: Partial<userOwnTractorSurveyStateType> = {
      ownTractorIdForEdit: myOwnTractor?.id,
    };

    if (myOwnTractor.machine?.approvalStatus === APPROVAL || myOwnTractor.machine?.approvalStatus === PSWAITING) {
      Object.assign(payload, {
        isApprovedTractor: true,
        manufacturedYear: myOwnTractor?.manufacturedYear,
        operationTime: myOwnTractor?.operationTime,
        brand: myOwnTractor.brand.name,
        brandId: myOwnTractor.brand.id,
        model: myOwnTractor.machine.model,
        engineOutputInPs: myOwnTractor.machine.engineOutputInPs,
      });
    }
    dispatch(userOwnTractorActions.changeOwnTractorSurveyForEdit(payload));
    navigate('/survey/progress/own-tractor/required-input-overview');
  };

  const handleSellTractorClick = (position: 'price-suggest-modal' | 'request-check-page') => {
    if (!myOwnTractor) {
      return;
    }
    ampli.pvmClickMySellOwnTractor({
      page_type: 'PUBLIC',
      position,
    });
    ReactGA.event('pvm_click_sell_tractor_button', {
      isModal: position === 'price-suggest-modal',
    });
    dispatch(userSellTractorSurveyActions.changeUserOwnTractorId(myOwnTractor.id));
    dispatch(
      userSellTractorSurveyActions.changeSellTractorRequiredInputs({
        brand: myOwnTractor?.brand?.name,
        brandId: myOwnTractor?.brand?.id,
        model: myOwnTractor?.machine?.model,
        engineOutputInPs:
          myOwnTractor?.machine.approvalStatus === APPROVAL ? myOwnTractor?.machine.engineOutputInPs : undefined,
        manufacturedYear: myOwnTractor?.manufacturedYear,
        operationTime: myOwnTractor?.operationTime,
      }),
    );
    if (selectedSuggestedPrice && selectedSuggestedPrice.suggestedPrice) {
      dispatch(userSellTractorSurveyActions.changeHopeSellingPrice(selectedSuggestedPrice.suggestedPrice / 10000));
    }
    navigate(sellTractorPageInfo.overviewPage.path, { replace: true });
  };

  const handlePriceSuggestModalClose = () => {
    ampli.pvmClickPriceSuggestModalClose();
    setSelectedSuggestedPrice(null);
    setIsPriceSuggestModalOpen(false);
  };

  const handleDisablePoke = () => {
    if (!myOwnTractor || !myOwnTractor.id) {
      return;
    }
    ampli.pvmClickMyTogglePokeStatus({
      status: 'PRIVATE',
      position: 'request-check-page',
    });
    patchUsersOwnTractor(
      {
        ownTractorId: myOwnTractor.id,
        reqBody: {
          isPokeEnabled: false,
        },
      },
      {
        onSuccess: (data) => {
          navigate(pageInfo.myInfoOwnTractorListPage.path);
        },
      },
    );
  };

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: true }} headerTitle={'찔러보기 요청 확인'} />,
    fixedHeaderHeight: 48,
    fixedFooter: (
      <FixedFooterWrapper>
        <BlurredSection />
        <Button
          borderType={'square'}
          color={'filledPrimaryStrong'}
          size={'md'}
          text={{ content: '판매 할게요', size: 'md' }}
          onClick={() => handleSellTractorClick('request-check-page')}
        />
        <Button
          borderType={'square'}
          color={'filledPrimaryLight'}
          size={'md'}
          text={{ content: '판매 할 생각이 없어요', size: 'md' }}
          onClick={() => handleDisablePoke()}
        />
      </FixedFooterWrapper>
    ),
    fixedFooterHeight: 52,
  });

  useEffect(() => {
    if (isGetUsersOwnTractorSuccess) {
      updateUserHasNewPoke();
    }
  }, [isGetUsersOwnTractorSuccess, updateUserHasNewPoke]);

  if (isGetUsersOwnTractorLoading) return <AnimateLoading />;

  if (myOwnTractor) {
    return (
      <>
        <Container>
          <InfoSection>
            <InfoTitleHighlight>{pokingDetailTitle}</InfoTitleHighlight>
            <InfoTitle>{'다른 회원이 제안한 내 트랙터\n가격을 확인 해보세요!'}</InfoTitle>
            <InfoDescription>
              {'다른 회원이 내 트랙터를\n구경하고 찔러보기를'}
              <InfoHighlight>{` ${myOwnTractor.pokeCount || 0} 건 `}</InfoHighlight>
              {'했어요!'}
            </InfoDescription>
          </InfoSection>
          {myOwnTractor.pokeCount > 0 && myOwnTractor.suggestedPrices?.length > 0 ? (
            <PokeRequestCheckCardGroup>
              {myOwnTractor.suggestedPrices.map((suggestedPrice: ITractorSuggestedPrice) => (
                <PokeRequestCheckCard
                  key={suggestedPrice.id}
                  onClick={() => {
                    ampli.pvmClickPriceSuggestCard({
                      price: suggestedPrice.suggestedPrice || 0,
                      pokingId: suggestedPrice.id,
                      createAt: suggestedPrice.createdAt,
                    });
                    setSelectedSuggestedPrice(suggestedPrice);
                    if (suggestedPrice && suggestedPrice.suggestedPrice) {
                      setIsFeedbackModalOpen(true);
                      return;
                    }
                    setIsPriceSuggestModalOpen(true);
                  }}
                >
                  <PokeRequestCheckCardBody>
                    <PokeRequestCheckCardHeader>
                      <Chip
                        type={'round'}
                        theme={'strong'}
                        color={'secondary'}
                        size={'sm'}
                        icon={{ name: 'CircleNew', position: 'left', size: 'Medium', fill: '#FFFFFF' }}
                      >
                        {'신규'}
                      </Chip>
                      <CardHeaderTitle>{formatDateFromTodayOnlyDifference(suggestedPrice.createdAt)}</CardHeaderTitle>
                    </PokeRequestCheckCardHeader>

                    <PokeRequestCheckCardContent>
                      {suggestedPrice.suggestedPrice ? (
                        <>
                          <ContentStrong>{`${formatPrice(suggestedPrice.suggestedPrice)}원`}</ContentStrong>
                          {'에 '}
                        </>
                      ) : null}
                      {'직거래 등록 제안이 왔어요'}
                    </PokeRequestCheckCardContent>
                  </PokeRequestCheckCardBody>
                  <PokeRequestCheckCardAction>
                    <Icon name={'AngleRight'} size={'Large'} fill={'#232429'} />
                  </PokeRequestCheckCardAction>
                </PokeRequestCheckCard>
              ))}
            </PokeRequestCheckCardGroup>
          ) : (
            <EmptySection>
              <EmptyCard>
                <Icon name={'LightBulb'} width={60} height={60} />
                <EmptyDescription>{'내가 보유한 트랙터 정보를\n정확히 보충하면 상단 노출돼요!'}</EmptyDescription>
              </EmptyCard>
              <EmptyButtonWrapper>
                <Button
                  borderType={'square'}
                  color={'filledGreyLight'}
                  size={'md'}
                  text={{ content: '트랙터 정보 수정하기', size: 'md' }}
                  onClick={handleEditOwnTractor}
                />
              </EmptyButtonWrapper>
            </EmptySection>
          )}
        </Container>

        {selectedSuggestedPrice && (
          <PopupConfirmModal
            isShow={isFeedbackModalOpen}
            title={'제안 가격에 응답해보세요!'}
            element={
              <FeedbackPopupBody
                ownTractorId={myOwnTractor.id}
                model={myOwnTractor.machine.model}
                suggestedPrice={selectedSuggestedPrice as ITractorSuggestedPrice}
                refetchMyOwnTractor={refetchMyOwnTractor}
                closeModal={() => {
                  setIsFeedbackModalOpen(false);
                  setSelectedSuggestedPrice(null);
                }}
              />
            }
          />
        )}

        <PopupConfirmModal
          isShow={isPriceSuggestModalOpen}
          closeModal={handlePriceSuggestModalClose}
          title={'직거래 등록 제안이 왔어요!'}
          description={'등록해보시겠어요?\n2분이면 등록 가능해요!'}
          buttonArray={[
            {
              text: '직거래 트랙터로 등록하기',
              onClick: () => handleSellTractorClick('price-suggest-modal'),
              color: 'blue',
            },
            {
              text: '닫기',
              onClick: handlePriceSuggestModalClose,
              color: 'gray',
            },
          ]}
        />
      </>
    );
  }
  return null;
};

const FixedFooterWrapper = styled.div`
  display: flex;
  padding: 0px 16px 32px 16px;
  background-color: white;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const BlurredSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  position: absolute;
  top: -40px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
`;

const InfoSection = styled.div`
  display: flex;
  padding: 32px 16px;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  align-self: stretch;
`;

const InfoTitle = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;
  white-space: pre-line;

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const InfoTitleHighlight = styled(InfoTitle)`
  color: var(--Light-Scheme-Color-primary-blue, #196eff);
`;

const InfoDescription = styled.pre`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const InfoHighlight = styled.span`
  color: var(--Light-Scheme-Color-primary-blue, #196eff);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%;
`;

const PokeRequestCheckCardGroup = styled.div`
  display: flex;
  padding: 0px 16px 32px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const PokeRequestCheckCard = styled.div`
  display: flex;
  height: 80px;
  padding: 16px 24px;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  border-radius: var(--radius-large, 16px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  cursor: pointer;
`;

const PokeRequestCheckCardBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
`;

const PokeRequestCheckCardHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

const CardHeaderTitle = styled.div`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */
`;

const PokeRequestCheckCardContent = styled.div`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
`;

const ContentStrong = styled.span`
  font-weight: 700;
`;
const PokeRequestCheckCardAction = styled.div``;

const EmptySection = styled.div`
  display: flex;
  padding: 30px 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  flex: 1 0 0;
  align-self: stretch;
`;

const EmptyCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const EmptyDescription = styled.div`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-align: center;
  white-space: pre-line;

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
`;

const EmptyButtonWrapper = styled.div``;
